import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Menu, Phone, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "Products", path: "/products" },
  { label: "Services", path: "/services" },
  { label: "Contact", path: "/contact" },
  { label: "FAQ", path: "/faq" },
];

const Logo = () => (
  <Link to="/" className="flex items-center" data-testid="logo-link">
    <img
      src="/logo.webp"
      alt="Shri Ram Frozen Foods"
      className="h-14 w-auto object-contain"
    />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
        ? "bg-white/95 backdrop-blur-md shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
        : "bg-white/80 backdrop-blur-sm"
        }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <nav className="flex items-center justify-between h-16 md:h-[72px]">
          <Logo />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`text-sm font-medium transition-colors duration-200 hover:text-[#2B7A1E] ${isActive(link.path) ? "text-[#2B7A1E] nav-link-active" : "text-[#333]"
                  }`}
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+918308203097"
              className="flex items-center gap-2 text-sm text-[#333] hover:text-[#2B7A1E] transition-colors"
              data-testid="nav-phone"
            >
              <Phone size={15} />
              <span className="font-medium">+91 83082 03097</span>
            </a>
            <Link to="/contact">
              <Button
                className="rounded-full bg-[#2B7A1E] hover:bg-[#236618] text-white font-semibold px-6 shadow-lg shadow-green-900/15"
                data-testid="nav-get-quote-btn"
              >
                Get a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu size={24} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-white p-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b">
                    <Logo />
                  </div>
                  <div className="flex-1 py-4">
                    {NAV_LINKS.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                        className={`block px-6 py-3.5 text-base font-medium transition-colors ${isActive(link.path)
                          ? "text-[#2B7A1E] bg-green-50 border-r-3 border-[#2B7A1E]"
                          : "text-[#333] hover:bg-gray-50"
                          }`}
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                  <div className="p-6 border-t space-y-3">
                    <a
                      href="tel:+918308203097"
                      className="flex items-center gap-2 text-sm text-[#333]"
                    >
                      <Phone size={15} />
                      <span>+91 83082 03097</span>
                    </a>
                    <Link to="/contact" onClick={() => setOpen(false)}>
                      <Button className="w-full rounded-full bg-[#2B7A1E] hover:bg-[#236618] text-white font-semibold">
                        Get a Quote
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
};
